import { Component, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { PublicInvitation } from 'src/app/models/membership.model';
import { AuthService } from 'src/app/services/auth.service';
import { MembershipService } from 'src/app/services/membership.service';
import { versions } from 'src/app/versions';

type Field = 'first_name' | 'last_name' | 'password';

@Component({
  selector: 'app-invitation',
  templateUrl: './invitation.component.html',
  styleUrls: ['./invitation.component.scss'],
  standalone: false,
})
export class InvitationComponent implements OnInit {
  readonly versions = versions;
  readonly loading = signal(true);
  readonly invitation = signal<PublicInvitation | null>(null);
  readonly invalidLink = signal(false);
  readonly submitting = signal(false);
  readonly formError = signal<string | null>(null);
  readonly showPassword = signal(false);

  private token = '';

  form = new FormGroup({
    first_name: new FormControl('', Validators.required),
    last_name: new FormControl('', Validators.required),
    password: new FormControl('', [Validators.required, Validators.minLength(8)]),
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private membership: MembershipService,
    private authService: AuthService,
  ) {}

  async ngOnInit() {
    this.token = this.route.snapshot.paramMap.get('token') ?? '';
    try {
      this.invitation.set(await lastValueFrom(this.membership.getInvitation(this.token)));
    } catch {
      this.invalidLink.set(true);
    }
    this.loading.set(false);
  }

  invalid(name: Field): boolean {
    const control = this.form.get(name);
    return !!control && control.touched && control.invalid;
  }

  has(name: Field, error: string): boolean {
    const control = this.form.get(name);
    return !!control && control.touched && control.hasError(error);
  }

  serverError(name: Field): string | null {
    return this.form.get(name)?.getError('serverError') ?? null;
  }

  accept(): void {
    this.form.markAllAsTouched();
    this.formError.set(null);
    if (this.form.invalid || this.submitting()) return;

    this.submitting.set(true);
    this.authService.acceptInvitation(this.token, this.form.getRawValue() as any).subscribe({
      next: () => this.router.navigate(['/main/dashboard']),
      error: (err: any) => {
        this.submitting.set(false);
        const errors = err?.error ?? err;
        let fieldError = false;
        for (const name of ['first_name', 'last_name', 'password'] as Field[]) {
          const message = errors?.[name];
          if (message) {
            this.form.get(name)?.setErrors({ serverError: Array.isArray(message) ? message[0] : String(message) });
            fieldError = true;
          }
        }
        if (!fieldError) this.formError.set(errors?.detail ?? 'Something went wrong. Please try again.');
      },
    });
  }
}
